/* NutriFitt × Symbiote — Deadly Nightshade page (nf-nightshade).
   The stone: WebGL1 symbiote goo + lantern + rain over the hero render. The goo is a little fluid field:
   you paint it with the pointer, it spreads, thick goo runs down in drips, and the host keeps crawling
   out of the dirt on its own. Draws only while on screen. Also: packs, fly to cart, rites panels,
   session pairing, facts viewer, sticky bar, chapter rail. One file, no libraries. */
(function () {
  'use strict';
  var html = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  function q(s, r) { return (r || document).querySelector(s); }
  function qa(s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); }
  function clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }
  function buzz(p) { try { if (navigator.vibrate) navigator.vibrate(p); } catch (e) {} }
  function money(cents, fmt) {
    var v = (Number(cents) || 0) / 100;
    var n2 = v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var n0 = Math.round(v).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    fmt = fmt || '$' + '{{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, k) {
      if (k === 'amount_no_decimals') return n0;
      if (k === 'amount_with_comma_separator') return v.toFixed(2).replace('.', ',');
      return n2;
    });
  }

  /* ───────────────────────── the stone (WebGL) ───────────────────────── */
  function Stone(wrap, onWake) {
    var cv = q('[data-ns-gl]', wrap);
    if (!cv || reduced.matches || !cv.dataset.nsSrc) return null;
    var gl = null;
    try { gl = cv.getContext('webgl', { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, powerPreference: 'high-performance' }); } catch (e) { gl = null; }
    if (!gl) return null;

    var VS = 'attribute vec2 a;varying vec2 v;void main(){v=a*.5+.5;gl_Position=vec4(a,0.,1.);}';
    var SIM = [
      'precision highp float;',
      'varying vec2 v;',
      'uniform sampler2D uPrev;uniform vec2 uPx;uniform vec4 uS[8];uniform float uN;uniform float uAsp;',
      'float h11(float n){return fract(sin(n*91.345)*47453.5453);}',
      'void main(){',
      '  float c=texture2D(uPrev,v).r;',
      '  float l=texture2D(uPrev,v-vec2(uPx.x,0.)).r,r=texture2D(uPrev,v+vec2(uPx.x,0.)).r;',
      '  float d=texture2D(uPrev,v-vec2(0.,uPx.y)).r,u=texture2D(uPrev,v+vec2(0.,uPx.y)).r;',
      '  float f=mix(c,(l+r+u+d)*.25,.2);',
      '  float col=h11(floor(v.x/uPx.x*.34)+7.);',
      '  float run=smoothstep(.6,.98,u)*u*.955*smoothstep(.3,.72,col);',
      '  f=max(f,run);',
      '  f=f*.9962-.0021;',
      '  for(int i=0;i<8;i++){if(float(i)>=uN)break;vec4 s=uS[i];vec2 q=(v-s.xy)*vec2(uAsp,1.);f+=s.w*exp(-dot(q,q)/(s.z*s.z));}',
      '  gl_FragColor=vec4(clamp(f,0.,1.),0.,0.,1.);',
      '}'
    ].join('\n');
    var REN = [
      'precision highp float;',
      'varying vec2 v;',
      'uniform sampler2D uImg,uGoo;uniform vec2 uRes,uImgRes,uGooPx,uPtr;uniform float uTime,uFlash,uLamp,uAsp;',
      'float h21(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}',
      'vec2 cover(vec2 uv){float ra=uRes.x/uRes.y,ri=uImgRes.x/uImgRes.y;vec2 s=ra>ri?vec2(1.,ri/ra):vec2(ra/ri,1.);return (uv-.5)*s+.5;}',
      'float rain(vec2 uv,float sc,float sp,float th){',
      '  vec2 p=vec2(uv.x*sc+uv.y*sc*.07,uv.y*sc*.11+uTime*sp);',
      '  vec2 id=floor(p);vec2 f=fract(p);float rn=h21(id+vec2(3.1,0.));',
      '  float on=step(th,rn);float x=abs(f.x-.5-(rn-.5)*.6);',
      '  return on*smoothstep(.07,0.,x)*smoothstep(0.,.7,f.y)*smoothstep(1.,.7,f.y);',
      '}',
      'void main(){',
      '  vec2 uv=v;',
      '  float g=texture2D(uGoo,uv).r;',
      '  float gx=texture2D(uGoo,uv+vec2(uGooPx.x,0.)).r-texture2D(uGoo,uv-vec2(uGooPx.x,0.)).r;',
      '  float gy=texture2D(uGoo,uv+vec2(0.,uGooPx.y)).r-texture2D(uGoo,uv-vec2(0.,uGooPx.y)).r;',
      '  vec3 N=normalize(vec3(-gx*3.4,-gy*3.4,1.));',
      '  float cov=smoothstep(.2,.52,g);',
      '  vec3 img=texture2D(uImg,cover(uv+N.xy*.028*smoothstep(.04,.4,g))).rgb;',
      '  vec2 dp=(uv-uPtr)*vec2(uAsp,1.);',
      '  float lamp=exp(-dot(dp,dp)/.04)*uLamp;',
      '  float lum=dot(img,vec3(.299,.587,.114));',
      '  vec3 base=img*(.92+.4*lamp);',
      '  base=mix(base,vec3(lum)*vec3(.78,.62,1.08),.1*(1.-lamp));',
      '  base+=lamp*vec3(.2,.05,.26)*.5;',
      '  float rn=rain(uv,34.,1.5,.84)*.6+rain(uv*1.7+.31,58.,2.3,.88)*.4;',
      '  base+=rn*vec3(.62,.55,.85)*(.18+.7*lamp+uFlash)*(1.-cov);',
      '  vec3 L=normalize(vec3(-.45,.65,.62));',
      '  float spec=pow(max(dot(reflect(-L,N),vec3(0.,0.,1.)),0.),38.);',
      '  float fres=pow(1.-clamp(N.z,0.,1.),1.5);',
      '  vec3 rimc=mix(vec3(.55,.16,1.),vec3(1.,.24,.78),clamp(uv.y*1.3-.1,0.,1.));',
      '  vec3 gc=vec3(.012,.006,.02)+fres*rimc*1.45+spec*vec3(1.,.9,1.)*1.15;',
      '  gc+=lamp*rimc*fres*.6+uFlash*fres*.8;',
      '  vec3 col=mix(base,gc,cov*.95);',
      '  float edge=smoothstep(.1,.2,g)*(1.-smoothstep(.22,.34,g));',
      '  col+=edge*vec3(.62,.16,.92)*.55;',
      '  col+=uFlash*(img*.7+vec3(.32,.26,.55)*.35);',
      '  vec2 o=uv-.5;col*=1.-dot(o,o)*.42;',
      '  col+=(h21(uv*uRes+fract(uTime*.37)*91.)-.5)*.03;',
      '  gl_FragColor=vec4(col,1.);',
      '}'
    ].join('\n');

    function shader(type, src) {
      var s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
      return s;
    }
    function program(fs) {
      var p = gl.createProgram();
      gl.attachShader(p, shader(gl.VERTEX_SHADER, VS)); gl.attachShader(p, shader(gl.FRAGMENT_SHADER, fs));
      gl.bindAttribLocation(p, 0, 'a'); gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(p));
      var u = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
      for (var i = 0; i < n; i++) { var info = gl.getActiveUniform(p, i); u[info.name.replace('[0]', '')] = gl.getUniformLocation(p, info.name); }
      return { p: p, u: u };
    }
    function target(w, h) {
      var t = gl.createTexture(); gl.bindTexture(gl.TEXTURE_2D, t);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      var f = gl.createFramebuffer(); gl.bindFramebuffer(gl.FRAMEBUFFER, f);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
      gl.clearColor(0, 0, 0, 1); gl.clear(gl.COLOR_BUFFER_BIT);
      return { t: t, f: f };
    }

    var sim, ren;
    try { sim = program(SIM); ren = program(REN); } catch (e) { return null; }
    var buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0); gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    var SW = 144, SH = 180;
    var A = target(SW, SH), B = target(SW, SH);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    var tex = gl.createTexture(), imgW = 4, imgH = 5, ready = false;

    var coarse = !fine.matches;
    var visible = false, paused = false, raf = 0, last = 0, time = 0, born = 0, drew = false;
    var ptr = { x: .5, y: .55, on: 0, lx: -1, ly: -1, t: -1e9 };
    var lamp = { x: .5, y: .55, k: .55 };
    var splats = [];
    var flash = { t: -1, next: 0 };
    var woke = false;

    function resize() {
      var r = cv.getBoundingClientRect(); if (!r.width) return;
      var dpr = Math.min(window.devicePixelRatio || 1, coarse ? 2 : 1.6);
      var w = Math.min(1400, Math.round(r.width * dpr)), h = Math.round(w * r.height / r.width);
      if (cv.width !== w || cv.height !== h) { cv.width = w; cv.height = h; }
    }
    var img = new Image(); img.crossOrigin = 'anonymous'; img.decoding = 'async';
    img.onload = function () {
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      imgW = img.naturalWidth; imgH = img.naturalHeight; ready = true; born = time; wake();
    };
    var pic = q('.ns-stone__img', wrap);
    img.src = (function () {
      var r = cv.getBoundingClientRect(), need = Math.min(1400, r.width * Math.min(window.devicePixelRatio || 1, coarse ? 2 : 1.6));
      var cs = (pic && pic.currentSrc) || '', m = cs.match(/[?&]width=(\d+)/);
      return m && +m[1] >= need * .9 ? cs : cv.dataset.nsSrc;
    })();

    function splat(x, y, r, a) { if (splats.length < 8) splats.push([x, y, r, a]); }
    function local(e) {
      var r = cv.getBoundingClientRect();
      return [clamp((e.clientX - r.left) / r.width, 0, 1), clamp(1 - (e.clientY - r.top) / r.height, 0, 1)];
    }
    function onMove(e) {
      var p = local(e);
      if (ptr.lx < 0) { ptr.lx = p[0]; ptr.ly = p[1]; }
      ptr.x = p[0]; ptr.y = p[1]; ptr.on = 1; ptr.t = time;
      if (!woke) { woke = true; if (onWake) onWake(); }
      wake();
    }
    function onDown(e) { onMove(e); var p = local(e); for (var i = 0; i < 3; i++) splat(p[0] + (Math.random() - .5) * .04, p[1] + (Math.random() - .5) * .04, .05, .32); }
    wrap.addEventListener('pointermove', onMove, { passive: true });
    wrap.addEventListener('pointerdown', onDown, { passive: true });
    wrap.addEventListener('pointerleave', function () { ptr.on = 0; ptr.lx = -1; }, { passive: true });

    function frame(now) {
      raf = 0;
      if (!ready || !visible || paused || document.hidden) { last = 0; return; }
      var dt = last ? Math.min(50, now - last) : 16; last = now; time += dt;
      var t = time / 1000;

      /* pointer stroke → splats along the path */
      if (ptr.on && ptr.lx >= 0) {
        var dx = ptr.x - ptr.lx, dy = ptr.y - ptr.ly, dist = Math.hypot(dx, dy);
        var steps = Math.min(5, Math.max(1, Math.ceil(dist / .014)));
        var amt = clamp(dist * 6, .05, .3);
        if (dist > .0005) for (var s = 1; s <= steps; s++) splat(ptr.lx + dx * s / steps, ptr.ly + dy * s / steps, coarse ? .055 : .045, amt);
        ptr.lx = ptr.x; ptr.ly = ptr.y;
      }
      if (time - ptr.t > 2600) ptr.on = 0;
      /* the host crawls out of the dirt by itself */
      var age = (time - born) / 1000;
      var boost = age < 1.6 ? (1 - age / 1.6) * .22 : 0;
      for (var i = 0; i < 3 && splats.length < 8; i++) {
        var k = t * .23 + i * 2.17;
        splat(.5 + .44 * Math.sin(k * 1.31 + i) * Math.cos(k * .53), .035 + .06 * (.5 + .5 * Math.sin(k * 2.1 + i * 1.7)), .036, .05 + boost);
      }
      if (splats.length < 8) { var side = Math.sin(t * .17) > 0 ? .035 : .965; splat(side, .12 + .34 * (.5 + .5 * Math.sin(t * .41)), .026, .045); }

      /* lantern follows the pointer, wanders when idle */
      var tx = ptr.on ? ptr.x : .5 + .2 * Math.sin(t * .31), ty = ptr.on ? ptr.y : .52 + .14 * Math.sin(t * .23 + 1.3);
      lamp.x += (tx - lamp.x) * .12; lamp.y += (ty - lamp.y) * .12; lamp.k += ((ptr.on ? 1 : .55) - lamp.k) * .05;

      /* lightning */
      if (!flash.next) flash.next = time + 3500 + Math.random() * 4000;
      if (time > flash.next) { flash.t = time; flash.next = time + 9000 + Math.random() * 8000; }
      var fl = 0;
      if (flash.t >= 0) {
        var ft = (time - flash.t) / 1000;
        fl = Math.exp(-Math.pow(ft - .04, 2) / .0009) + .65 * Math.exp(-Math.pow(ft - .2, 2) / .0024) + .25 * Math.exp(-Math.pow(ft - .36, 2) / .002);
        if (ft > .7) flash.t = -1;
      }

      /* sim pass */
      gl.useProgram(sim.p);
      gl.bindFramebuffer(gl.FRAMEBUFFER, B.f); gl.viewport(0, 0, SW, SH);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, A.t);
      gl.uniform1i(sim.u.uPrev, 0); gl.uniform2f(sim.u.uPx, 1 / SW, 1 / SH);
      gl.uniform1f(sim.u.uAsp, SW / SH); gl.uniform1f(sim.u.uN, splats.length);
      var arr = new Float32Array(32);
      for (var j = 0; j < splats.length; j++) { arr[j * 4] = splats[j][0]; arr[j * 4 + 1] = splats[j][1]; arr[j * 4 + 2] = splats[j][2]; arr[j * 4 + 3] = splats[j][3]; }
      gl.uniform4fv(sim.u.uS, arr);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      var sw = A; A = B; B = sw; splats.length = 0;

      /* render pass */
      gl.useProgram(ren.p);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null); gl.viewport(0, 0, cv.width, cv.height);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, A.t);
      gl.uniform1i(ren.u.uImg, 0); gl.uniform1i(ren.u.uGoo, 1);
      gl.uniform2f(ren.u.uRes, cv.width, cv.height); gl.uniform2f(ren.u.uImgRes, imgW, imgH);
      gl.uniform2f(ren.u.uGooPx, 1 / SW, 1 / SH); gl.uniform2f(ren.u.uPtr, lamp.x, lamp.y);
      gl.uniform1f(ren.u.uTime, t); gl.uniform1f(ren.u.uFlash, fl * .9); gl.uniform1f(ren.u.uLamp, lamp.k);
      gl.uniform1f(ren.u.uAsp, cv.width / cv.height);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      if (!drew) { drew = true; wrap.classList.add('is-gl'); }
      raf = requestAnimationFrame(frame);
    }
    function wake() { if (!raf && ready && visible && !paused && !document.hidden) raf = requestAnimationFrame(frame); }

    resize();
    if ('ResizeObserver' in window) new ResizeObserver(resize).observe(cv); else window.addEventListener('resize', resize);
    if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { visible = es[0].isIntersecting; wake(); }, { rootMargin: '80px' }).observe(wrap);
    else visible = true;
    document.addEventListener('visibilitychange', wake);
    cv.addEventListener('webglcontextlost', function (e) { e.preventDefault(); ready = false; wrap.classList.remove('is-gl'); }, false);

    return {
      pause: function (on) { paused = on; if (!on) wake(); },
      flash: function () { flash.t = time; wake(); },
      burst: function () { for (var i = 0; i < 8; i++) splat(.15 + Math.random() * .7, .06 + Math.random() * .25, .06, .35); wake(); }
    };
  }

  /* ───────────────────────── page ───────────────────────── */
  function init(root) {
    if (root.__ns) return; root.__ns = true;
    var D = root.dataset, fmt = D.moneyFormat, servings = +D.servings || 20, variant = +D.variant;
    var cartUrl = D.cartUrl || '/cart';
    var state = { qty: 1, cents: +D.cents, full: +D.cents, name: 'Single tub' };
    var stone = null;

    /* header offset for sticky stage + rail jumps */
    var header = document.querySelector('#pageheader, .section-header, header');
    function setHH() { var h = header ? header.getBoundingClientRect().height : 70; root.style.setProperty('--hh', Math.round(clamp(h, 50, 140)) + 'px'); }
    setHH(); window.addEventListener('resize', setHH, { passive: true });

    /* stone */
    var stoneEl = q('[data-ns-stone]', root);
    if (stoneEl) {
      try { stone = Stone(stoneEl, function () { stoneEl.classList.add('is-woken'); }); } catch (e) { stone = null; }
      if (stone) root.classList.add('is-gl-ok');
      var seal = q('[data-ns-seal]', root), pauseBtn = q('[data-ns-pause]', root);
      if (pauseBtn) pauseBtn.addEventListener('click', function () {
        var on = !seal.classList.contains('is-paused');
        seal.classList.toggle('is-paused', on); pauseBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
        pauseBtn.setAttribute('aria-label', on ? 'Play the stone' : 'Pause the stone');
        if (stone) stone.pause(on);
      });
      if (fine.matches && !reduced.matches) {
        var stage = q('[data-ns-stage]', root);
        stage.addEventListener('pointermove', function (e) {
          var r = stoneEl.getBoundingClientRect(), x = (e.clientX - r.left) / r.width - .5, y = (e.clientY - r.top) / r.height - .5;
          stoneEl.style.setProperty('--ty', (x * 7).toFixed(2) + 'deg'); stoneEl.style.setProperty('--tx', (-y * 5).toFixed(2) + 'deg');
        }, { passive: true });
        stage.addEventListener('pointerleave', function () { stoneEl.style.setProperty('--ty', '0deg'); stoneEl.style.setProperty('--tx', '0deg'); });
      }
    }

    /* packs */
    var packBtns = qa('[data-ns-pack]', root);
    var mainPacks = packBtns.filter(function (b) { return b.hasAttribute('data-cents'); });
    var stickySel = q('[data-ns-sticky-sel]', root);
    function packData(n) { for (var i = 0; i < mainPacks.length; i++) if (+mainPacks[i].dataset.nsPack === n) return mainPacks[i]; return mainPacks[0]; }
    function render() {
      var c = state.cents;
      qa('[data-ns-price]', root).forEach(function (el) { el.textContent = money(c, fmt); });
      qa('[data-ns-was]', root).forEach(function (el) { if (c < state.full) { el.textContent = money(state.full, fmt); el.hidden = false; } else el.hidden = true; });
      qa('[data-ns-per]', root).forEach(function (el) { el.textContent = money(Math.round(c / (state.qty * servings)), fmt) + ' / serving'; });
      qa('[data-ns-cta-price], [data-ns-pack-total]', root).forEach(function (el) { el.textContent = money(c, fmt); });
      qa('[data-ns-pack-name]', root).forEach(function (el) { el.textContent = state.name; });
      var qin = q('[data-ns-qty]', root); if (qin) qin.value = state.qty;
      if (stickySel) stickySel.value = String(state.qty);
    }
    function setPack(n, focus) {
      var src = packData(n); if (!src) return;
      state.qty = +src.dataset.nsPack; state.cents = +src.dataset.cents; state.full = +src.dataset.full; state.name = src.dataset.name;
      packBtns.forEach(function (b) { var on = +b.dataset.nsPack === state.qty; b.classList.toggle('is-on', on); b.setAttribute('aria-checked', on ? 'true' : 'false'); b.tabIndex = on ? 0 : -1; });
      if (focus) focus.focus();
      render(); buzz(5);
    }
    packBtns.forEach(function (b) {
      b.addEventListener('click', function () { setPack(+b.dataset.nsPack); });
      b.addEventListener('keydown', function (e) {
        if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].indexOf(e.key) < 0) return;
        e.preventDefault();
        var group = qa('[data-ns-pack]', b.parentNode), i = group.indexOf(b);
        var nx = group[(i + (e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : group.length - 1)) % group.length];
        setPack(+nx.dataset.nsPack, nx);
      });
    });
    if (stickySel) stickySel.addEventListener('change', function () { setPack(+stickySel.value); });

    /* cart */
    function addItems(items) {
      return fetch(D.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: items }) })
        .then(function (r) {
          return r.json().catch(function () { return {}; }).then(function (d) {
            if (!r.ok) throw new Error(d.description || d.message || 'Could not add to cart. Try again.');
            document.dispatchEvent(new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false }));
            setTimeout(function () { if (window.theme && theme.settings && theme.settings.cart_type === 'drawer') document.dispatchEvent(new CustomEvent('theme:open-cart-drawer', { bubbles: true, cancelable: false })); }, 650);
            return d;
          });
        });
    }
    var flyImg = q('.ns-sticky__img img', root) || q('.ns-arch--dn img', root);
    function fly(from) {
      if (reduced.matches || !flyImg || !from.animate) return;
      var cart = document.querySelector('#pageheader a[href$="/cart"], header a[href$="/cart"], a.cart-link, [data-cart-count], a[href="/cart"]');
      var a = from.getBoundingClientRect(), b = cart ? cart.getBoundingClientRect() : { left: innerWidth - 40, top: 20, width: 20, height: 20 };
      var el = document.createElement('img'); el.src = flyImg.currentSrc || flyImg.src; el.className = 'ns-fly'; el.alt = '';
      document.body.appendChild(el);
      var x0 = a.left + a.width / 2 - 45, y0 = a.top + a.height / 2 - 60, x1 = b.left + b.width / 2 - 45, y1 = b.top + b.height / 2 - 60;
      var mx = (x0 + x1) / 2, my = Math.min(y0, y1) - 140;
      el.animate([
        { transform: 'translate(' + x0 + 'px,' + y0 + 'px) scale(.4) rotate(0deg)', opacity: 0 },
        { transform: 'translate(' + x0 + 'px,' + (y0 - 40) + 'px) scale(1) rotate(-8deg)', opacity: 1, offset: .2 },
        { transform: 'translate(' + mx + 'px,' + my + 'px) scale(.8) rotate(10deg)', opacity: 1, offset: .6 },
        { transform: 'translate(' + x1 + 'px,' + y1 + 'px) scale(.18) rotate(24deg)', opacity: .2 }
      ], { duration: 950, easing: 'cubic-bezier(.5,0,.2,1)' }).onfinish = function () { el.remove(); };
    }
    function go(btn, items, okText, statusEl) {
      if (!btn || btn.disabled || btn.classList.contains('is-busy')) return Promise.resolve(false);
      var lab = q('[data-label]', btn) || btn, before = lab.textContent;
      btn.classList.add('is-busy'); lab.textContent = 'Bonding…'; if (statusEl) statusEl.textContent = '';
      fly(btn);
      return addItems(items).then(function () {
        btn.classList.remove('is-busy'); btn.classList.add('is-done'); lab.textContent = okText || 'In your cart';
        if (statusEl) statusEl.innerHTML = 'It’s yours. <a href="' + cartUrl + '">View cart</a> · <a href="/checkout">Checkout</a>';
        if (stone) { stone.flash(); stone.burst(); }
        buzz([14, 40, 22]);
        setTimeout(function () { btn.classList.remove('is-done'); lab.textContent = before; }, 2600);
        return true;
      }).catch(function (err) {
        btn.classList.remove('is-busy'); lab.textContent = before; if (statusEl) statusEl.textContent = err.message;
        return false;
      });
    }
    function mainItems() { return [{ id: variant, quantity: state.qty }]; }
    var form = q('.ns-form', root), addBtn = q('[data-ns-add]', root), mainStatus = form ? q('[data-ns-status]', form) : null;
    if (addBtn) {
      addBtn.type = 'button';
      addBtn.addEventListener('click', function (e) { e.preventDefault(); go(addBtn, mainItems(), 'Bonded · in your cart', mainStatus); });
      if (form) form.addEventListener('submit', function (e) { e.preventDefault(); go(addBtn, mainItems(), 'Bonded · in your cart', mainStatus); });
    }
    var stickyBtn = q('[data-ns-sticky-add]', root);
    if (stickyBtn) stickyBtn.addEventListener('click', function () { go(stickyBtn, mainItems(), 'In your cart', mainStatus); });

    /* session pairing */
    var pair = q('[data-ns-pair]', root);
    if (pair) {
      var psel = q('[data-ns-pair-sel]', pair), pprice = q('[data-ns-pair-price]', pair), ptotal = q('[data-ns-pair-total]', pair);
      var pa = q('.ns-pair-a', pair), pb = q('.ns-pair-b', pair), padd = q('[data-ns-pair-add]', pair);
      var pairRender = function () {
        var c = +pair.dataset.pairCents;
        if (pprice) pprice.textContent = money(c, fmt);
        if (ptotal) ptotal.textContent = money(c + (+D.cents), fmt);
      };
      if (psel) psel.addEventListener('change', function () {
        var o = psel.options[psel.selectedIndex];
        pair.dataset.pairVariant = o.value; pair.dataset.pairCents = o.dataset.cents; pair.dataset.side = o.dataset.side || 'a';
        if (pa && pb) { var b = o.dataset.side === 'b'; pa.classList.toggle('is-off', b); pb.classList.toggle('is-off', !b); }
        pairRender();
      });
      if (padd) padd.addEventListener('click', function () {
        go(padd, [{ id: variant, quantity: 1 }, { id: +pair.dataset.pairVariant, quantity: 1 }], 'Both in your cart', q('[data-ns-status]', pair));
      });
      pairRender();
    }

    /* sticky buy bar */
    var sticky = q('[data-ns-sticky]', root);
    if (sticky && addBtn && 'IntersectionObserver' in window) {
      var ctaVis = true, footVis = false, otherVis = 0;
      var foot = document.querySelector('#shopify-section-footer, .section-footer, footer');
      var sync = function () {
        var past = addBtn.getBoundingClientRect().bottom < 0;
        var on = !ctaVis && past && !footVis && otherVis === 0;
        sticky.classList.toggle('is-on', on); sticky.setAttribute('aria-hidden', on ? 'false' : 'true');
        qa('button, select', sticky).forEach(function (x) { x.tabIndex = on ? 0 : -1; });
      };
      new IntersectionObserver(function (es) { ctaVis = es[0].isIntersecting; sync(); }).observe(addBtn);
      if (foot) new IntersectionObserver(function (es) { footVis = es[0].isIntersecting; sync(); }).observe(foot);
      var others = [];
      var oio = new IntersectionObserver(function (es) { es.forEach(function (e) { var i = others.indexOf(e.target); if (e.isIntersecting && i < 0) others.push(e.target); if (!e.isIntersecting && i >= 0) others.splice(i, 1); }); otherVis = others.length; sync(); });
      qa('[data-ns-pair-add]', root).forEach(function (el) { oio.observe(el); });
      window.addEventListener('scroll', function () { if (!ctaVis) sync(); }, { passive: true });
    }

    /* story loop: plays only while on screen */
    qa('[data-ns-vid]', root).forEach(function (vid) {
      var c = navigator.connection;
      if (reduced.matches || (c && c.saveData) || !('IntersectionObserver' in window)) return;
      new IntersectionObserver(function (es) {
        if (es[0].isIntersecting) { if (!vid.getAttribute('src')) vid.src = vid.dataset.nsSrc; var pr = vid.play(); if (pr && pr.catch) pr.catch(function () {}); }
        else if (vid.getAttribute('src')) vid.pause();
      }, { rootMargin: '120px' }).observe(vid);
    });

    /* facts viewer */
    facts(root);

    /* chapter rail */
    var rail = q('[data-ns-rail]', root);
    if (rail && 'IntersectionObserver' in window) {
      var links = qa('[data-ns-rail-for]', rail);
      var mark = function (id) { links.forEach(function (a) { a.classList.toggle('is-on', a.dataset.nsRailFor === id); }); };
      var cio = new IntersectionObserver(function (es) { es.forEach(function (e) { if (e.isIntersecting) mark(e.target.dataset.nsChapter); }); }, { rootMargin: '-45% 0px -50% 0px' });
      qa('[data-ns-chapter]', root).forEach(function (s) { cio.observe(s); });
      links.forEach(function (a) {
        a.addEventListener('click', function (e) {
          var t = document.querySelector(a.getAttribute('href')); if (!t) return;
          e.preventDefault();
          var y = t.getBoundingClientRect().top + window.pageYOffset - (parseFloat(root.style.getPropertyValue('--hh')) || 70) + 1;
          window.scrollTo({ top: y, behavior: reduced.matches ? 'auto' : 'smooth' });
        });
      });
    }

    /* blade (ingredient rites) */
    var blade = q('[data-ns-blade]', root), bar = q('[data-ns-blade-bar]', root);
    if (blade) {
      var bsync = function () {
        var max = blade.scrollWidth - blade.clientWidth;
        if (!bar) return;
        var w = blade.clientWidth / Math.max(blade.scrollWidth, 1);
        bar.style.width = (w * 100).toFixed(1) + '%';
        bar.style.transform = 'translateX(' + (max > 0 ? (blade.scrollLeft / max) * (1 / w - 1) * 100 : 0).toFixed(1) + '%)';
      };
      blade.addEventListener('scroll', bsync, { passive: true }); window.addEventListener('resize', bsync, { passive: true }); bsync();
      if (fine.matches) {
        var drag = null, moved = 0;
        blade.addEventListener('pointerdown', function (e) { if (e.pointerType !== 'mouse' || blade.scrollWidth <= blade.clientWidth) return; drag = { x: e.clientX, s: blade.scrollLeft }; moved = 0; });
        window.addEventListener('pointermove', function (e) { if (!drag) return; var dx = e.clientX - drag.x; moved = Math.max(moved, Math.abs(dx)); if (moved > 4) { blade.style.scrollSnapType = 'none'; blade.scrollLeft = drag.s - dx; } });
        window.addEventListener('pointerup', function () { if (!drag) return; drag = null; blade.style.scrollSnapType = ''; });
        blade.addEventListener('click', function (e) { if (moved > 6) { e.preventDefault(); e.stopPropagation(); } }, true);
      }
    }

    /* rites: one open panel on wide screens (hover, focus or click) */
    var rites = qa('[data-ns-rite]', root), wide = window.matchMedia('(min-width: 1200px)'), riteT = 0;
    var openRite = function (r) { rites.forEach(function (x) { var on = x === r; x.classList.toggle('is-on', on); x.setAttribute('aria-expanded', on ? 'true' : 'false'); }); };
    rites.forEach(function (r) {
      r.addEventListener('mouseenter', function () { if (!wide.matches) return; clearTimeout(riteT); riteT = setTimeout(function () { openRite(r); }, 110); });
      r.addEventListener('mouseleave', function () { clearTimeout(riteT); });
      r.addEventListener('focus', function () { if (wide.matches) openRite(r); });
      r.addEventListener('click', function () { if (wide.matches) openRite(r); });
    });

    /* living goo: idle motion only on what is on screen */
    if ('IntersectionObserver' in window) {
      var lio = new IntersectionObserver(function (es) { es.forEach(function (e) { e.target.classList.toggle('is-live', e.isIntersecting && e.intersectionRatio > .35); }); }, { threshold: [0, .35, .7] });
      qa('[data-ns-rite], .ns-bond', root).forEach(function (el) { lio.observe(el); });
    }

    /* magnet buttons */
    if (fine.matches && !reduced.matches) qa('[data-ns-magnet]', root).forEach(function (b) {
      b.addEventListener('pointermove', function (e) {
        var r = b.getBoundingClientRect();
        b.style.setProperty('--mx', (((e.clientX - r.left) / r.width - .5) * 10).toFixed(1) + 'px');
        b.style.setProperty('--my', (((e.clientY - r.top) / r.height - .5) * 8).toFixed(1) + 'px');
      });
      b.addEventListener('pointerleave', function () { b.style.setProperty('--mx', '0px'); b.style.setProperty('--my', '0px'); });
    });

    /* reveal, words, bloom, counters */
    var counted = function (el) {
      var to = +el.dataset.nsCount; if (!to || reduced.matches) return;
      var t0 = performance.now(), dur = 1400;
      var step = function (now) {
        var k = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - k, 3);
        el.textContent = Math.round(to * e).toLocaleString('en-US');
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if ('IntersectionObserver' in window && !reduced.matches) {
      var io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in'); io.unobserve(e.target);
          qa('[data-ns-count]', e.target).forEach(counted);
        });
      }, { rootMargin: '0px 0px -10% 0px' });
      qa('[data-ns-reveal], [data-ns-words]', root).forEach(function (el) { io.observe(el); });
      root.classList.add('is-js');
      setTimeout(function () { qa('[data-ns-reveal], [data-ns-words]', root).forEach(function (el) { var r = el.getBoundingClientRect(); if (r.top < innerHeight && r.bottom > 0) el.classList.add('is-in'); }); }, 400);
    }

    render();
  }

  /* facts viewer: whole panel at 1×, drag, double-click / double-tap / pinch / wheel to zoom */
  function facts(root) {
    var dlg = q('[data-ns-facts]', root); if (!dlg) return;
    var view = q('[data-ns-facts-view]', dlg), img = q('[data-ns-facts-img]', dlg), zl = q('[data-ns-facts-z]', dlg);
    var Z = 1, ix = 0, iy = 0, ptrs = {}, pinch = null, drag = null, lastTap = 0, lx = 0, ly = 0;
    var ratio = function () { return (img.naturalWidth && img.naturalHeight) ? img.naturalWidth / img.naturalHeight : (+img.getAttribute('width') || 3) / (+img.getAttribute('height') || 4); };
    var base = function () { var vw = view.clientWidth, vh = view.clientHeight, ar = ratio(); return { vw: vw, vh: vh, ar: ar, w: Math.min(vw * .94, vh * .94 * ar) }; };
    var apply = function () {
      var b = base(), w = b.w * Z, h = w / b.ar;
      ix = w <= b.vw ? (b.vw - w) / 2 : clamp(ix, b.vw - w, 0);
      iy = h <= b.vh ? (b.vh - h) / 2 : clamp(iy, b.vh - h, 0);
      view.style.setProperty('--iw', w.toFixed(1) + 'px'); view.style.setProperty('--ix', ix.toFixed(1) + 'px'); view.style.setProperty('--iy', iy.toFixed(1) + 'px');
      if (zl) zl.textContent = (Math.round(Z * 10) / 10) + '×';
    };
    var center = function () { Z = 1; ix = 0; iy = 0; apply(); };
    var zoomAt = function (z, cx, cy) {
      var b = base(), w0 = b.w * Z, px = (cx - ix) / w0, py = (cy - iy) / (w0 / b.ar);
      Z = clamp(z, 1, 4); var w = b.w * Z; ix = cx - px * w; iy = cy - py * (w / b.ar); apply();
    };
    var local = function (e) { var r = view.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; };
    img.addEventListener('load', function () { if (dlg.open) center(); });
    qa('[data-ns-facts-open]', root).forEach(function (b) {
      b.addEventListener('click', function () {
        img.loading = 'eager';
        if (dlg.showModal) dlg.showModal(); else dlg.setAttribute('open', '');
        view.classList.add('is-drag'); requestAnimationFrame(function () { center(); requestAnimationFrame(function () { view.classList.remove('is-drag'); }); });
        buzz(6);
      });
    });
    dlg.addEventListener('click', function (e) { if (e.target === dlg || e.target.closest('[data-ns-close]')) dlg.close(); });
    window.addEventListener('resize', function () { if (dlg.open) apply(); }, { passive: true });
    view.addEventListener('pointerdown', function (e) {
      try { view.setPointerCapture(e.pointerId); } catch (x) {}
      ptrs[e.pointerId] = local(e);
      var ids = Object.keys(ptrs);
      if (ids.length === 2) { var a = ptrs[ids[0]], b = ptrs[ids[1]]; pinch = { d: Math.hypot(a[0] - b[0], a[1] - b[1]), z: Z }; drag = null; }
      else { drag = { x: ptrs[e.pointerId][0], y: ptrs[e.pointerId][1], ix: ix, iy: iy, moved: 0 }; view.classList.add('is-drag'); }
    });
    view.addEventListener('pointermove', function (e) {
      if (!ptrs[e.pointerId]) return;
      ptrs[e.pointerId] = local(e);
      var ids = Object.keys(ptrs);
      if (pinch && ids.length === 2) { var a = ptrs[ids[0]], b = ptrs[ids[1]], d = Math.hypot(a[0] - b[0], a[1] - b[1]); view.classList.add('is-drag'); zoomAt(pinch.z * d / pinch.d, (a[0] + b[0]) / 2, (a[1] + b[1]) / 2); return; }
      if (drag) { var p = ptrs[e.pointerId]; drag.moved = Math.max(drag.moved, Math.abs(p[0] - drag.x) + Math.abs(p[1] - drag.y)); ix = drag.ix + p[0] - drag.x; iy = drag.iy + p[1] - drag.y; apply(); }
    });
    var up = function (e) {
      var p = ptrs[e.pointerId]; delete ptrs[e.pointerId];
      if (Object.keys(ptrs).length < 2) pinch = null;
      if (drag && p && drag.moved < 8) {
        var now = performance.now();
        if (now - lastTap < 300 && Math.abs(p[0] - lx) < 30 && Math.abs(p[1] - ly) < 30) { zoomAt(Z < 1.9 ? 2 : Z < 2.9 ? 3 : 1, p[0], p[1]); lastTap = 0; buzz(6); }
        else { lastTap = now; lx = p[0]; ly = p[1]; }
      }
      if (!Object.keys(ptrs).length) { drag = null; view.classList.remove('is-drag'); }
    };
    view.addEventListener('pointerup', up); view.addEventListener('pointercancel', up);
    view.addEventListener('wheel', function (e) { e.preventDefault(); var p = local(e); view.classList.add('is-drag'); zoomAt(Z * (e.deltaY < 0 ? 1.12 : 1 / 1.12), p[0], p[1]); clearTimeout(view.__w); view.__w = setTimeout(function () { view.classList.remove('is-drag'); }, 120); }, { passive: false });
    dlg.addEventListener('keydown', function (e) {
      var b = base();
      if (e.key === '+' || e.key === '=') zoomAt(Z * 1.25, b.vw / 2, b.vh / 2);
      if (e.key === '-') zoomAt(Z / 1.25, b.vw / 2, b.vh / 2);
    });
  }

  /* recommendations ("Feed the host"): every product as a transparent cut-out so the cards sit inside the page */
  var CUT = ['pre-fitt-carnage-high-stim-pre-workout', 'epic-aminos', 'epic-greens', 'vita-fitt-high-performance-multivitamin', 'symbiote-thermo-intense-thermogenic', 'symbiote-pre-high-stim', 'carnage-bloodlust-pump-caps', 'krobaa-pre-workout', 'max-carnage-stack-pre-pump', 'epic-creatine', 'epic-whey', 'epic-collagen', 'regenesis', 'codex', 'silence', 'phage-test-booster', 'epic-electrolytes', 'crucifix', 'symbiote-pre-workout-sachets', 'nutrifitt-20-oz-shaker', 'epic-essentials', 'the-fuel-for-the-mind-stack'];
  var CUT_X = { 'carnage-pump': 'carnage-pump-terror-tart-transparent.png', 'nutrifitt-lee-holy-water': 'holy-water-blue-raspberry-cutout.png', 'symbiote-deadly-nightshade': '01-symbiote-deadly-nightshade-front-no-bg.webp' };
  function cutFor(h) { var f = CUT_X[h] || (CUT.indexOf(h) > -1 ? 'nf-card-' + h + '.png' : ''); return f ? '/cdn/shop/files/' + f : ''; }
  function recs() {
    var sec = document.querySelector('.section-product-recommendations'); if (!sec) return;
    var fix = function (blk) {
      var a = blk.querySelector('a[href*="/products/"]'); if (!a) return;
      var h = (a.getAttribute('href').split('/products/')[1] || '').split(/[?#\/]/)[0];
      var u = cutFor(h); if (!u) return;
      var set = [360, 540, 720, 900].map(function (w) { return u + '?width=' + w + ' ' + w + 'w'; }).join(', ');
      qa('.product-block__image--primary img', blk).forEach(function (im) {
        if (im.getAttribute('srcset') !== set) im.setAttribute('srcset', set);
        if (im.getAttribute('data-srcset') !== set) im.setAttribute('data-srcset', set);
        if (im.getAttribute('src') !== u + '?width=720') im.setAttribute('src', u + '?width=720');
      });
      var ii = blk.querySelector('.product-block__image--primary .inline-image'); if (ii) ii.style.setProperty('--full-url', 'url(' + u + '?width=1500)');
    };
    var scan = function () { qa('product-block, .product-block', sec).forEach(fix); };
    scan();
    new MutationObserver(function () { scan(); }).observe(sec, { childList: true, subtree: true, attributes: true, attributeFilter: ['src', 'srcset', 'data-srcset'] });
  }

  function boot() {
    qa('[data-ns]').forEach(init);
    recs();
    if (window.NFGoo && NFGoo.arrive) NFGoo.arrive({});
    else { html.classList.add('dn-revealed'); document.dispatchEvent(new CustomEvent('nfdn:revealed')); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { var r = e.target.querySelector('[data-ns]'); if (r) init(r); });
})();
